import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, ShieldCheck, Users } from "lucide-react";
import { useGetList } from "ra-core";

/**
 * UserRolesPage — Admin-only page showing all roles and their assigned permissions.
 * Data is fully dynamic: both roles and permissions are fetched from the DB.
 * Only users with 'manage_users' permission can access this page; others are redirected to /sales.
 */
export function UserRolesPage() {

  const { data: roles, isLoading: rolesLoading } = useGetList("roles", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "name", order: "ASC" },
  });

  const { data: rolePermissions, isLoading: rpLoading } = useGetList("role_permissions", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "role_slug", order: "ASC" },
  });

  const { data: permissions } = useGetList("permissions", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const { data: salesUsers } = useGetList("sales", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "first_name", order: "ASC" },
  });

  if (rolesLoading || rpLoading) {
    return (
      <div className="p-8 text-muted-foreground text-sm animate-pulse">
        Loading roles &amp; permissions…
      </div>
    );
  }

  // Build a look-up: role_slug → Set<permission_slug>
  const rolePermMap: Record<string, Set<string>> = {};
  for (const rp of rolePermissions ?? []) {
    if (!rolePermMap[rp.role_slug]) rolePermMap[rp.role_slug] = new Set();
    rolePermMap[rp.role_slug].add(rp.permission_slug);
  }

  // User count per role
  const userCountByRole: Record<string, number> = {};
  for (const user of salesUsers ?? []) {
    if (user.role) userCountByRole[user.role] = (userCountByRole[user.role] ?? 0) + 1;
  }

  return (
    <div className="max-w-4xl w-full mx-auto mt-6 space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck size={22} className="text-primary" />
        <div>
          <h1 className="text-xl font-bold">Roles &amp; Access</h1>
          <p className="text-sm text-muted-foreground">
            All roles and their permission assignments. Manage roles in the database.
          </p>
        </div>
      </div>

      {roles?.map((role) => {
        const permsForRole = rolePermMap[role.slug] ?? new Set();
        const userCount = userCountByRole[role.slug] ?? 0;

        return (
          <Card key={role.slug} className="overflow-hidden">
            <CardHeader className="pb-3 bg-muted/30">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Lock size={15} className="text-primary" />
                    {role.name}
                  </CardTitle>
                  {role.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{role.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Users size={13} />
                  {userCount} user{userCount !== 1 ? "s" : ""}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-3">
              {permissions && permissions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {permissions.map((perm) => {
                    const has = permsForRole.has(perm.slug);
                    return (
                      <Badge
                        key={perm.slug}
                        variant={has ? "default" : "outline"}
                        className={`text-xs ${
                          has
                            ? "bg-primary/15 text-primary border-primary/30 hover:bg-primary/20"
                            : "text-muted-foreground/40 border-muted-foreground/20"
                        }`}
                        title={perm.description ?? perm.slug}
                      >
                        {perm.name}
                      </Badge>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No permissions assigned.</p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

UserRolesPage.path = "/user-roles";
